import { useEffect, useState } from 'react';
import { ConstructorProvider } from './store/useConstructor';
import { ConstructorTopBar } from './ConstructorTopBar';
import { ConstructorPreview } from './ConstructorPreview';
import { DesignConstructorBody } from './DesignConstructor';
import { ALL_PREVIEW_DEVICES, DevicePreviewToggle, type PreviewDevice } from './DevicePreviewToggle';
import { ClassicPreviewFrame, DevicePreviewFrame } from './DevicePreviewFrame';
import { LabActiveContext } from './LabActiveContext';
import { LabSceneSelect } from './LabSceneSelect';
import { LIVE_LAB_SCENE, type LabPreviewSceneId } from './labScenes';
import { useSelectedComponent } from './useSelectedComponent';
import { useBaselineSync } from './useChildListener';
import { isComponentTemplate, type ComponentTemplate, type Scene } from './schema/components';
import type { ConstructorValue } from './types';

const STORAGE_KEY = 'design-constructor.preview-config';

interface PersistedConfig {
  template: ComponentTemplate;
  token: string;
  gameId: string;
}

function loadPersisted(): PersistedConfig | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedConfig>;
    return {
      template: isComponentTemplate(parsed.template) ? parsed.template : 'classic',
      token: typeof parsed.token === 'string' ? parsed.token : '',
      gameId: typeof parsed.gameId === 'string' ? parsed.gameId : '',
    };
  } catch {
    return null;
  }
}

/**
 * Initial config — URL query params take precedence (so shareable links
 * keep working), then localStorage fallback (so reload preserves last
 * entered token + game_id), then defaults.
 */
function readInitial(): PersistedConfig {
  const params = new URLSearchParams(window.location.search);
  const persisted = loadPersisted();
  const tplRaw = params.get('template');
  const template: ComponentTemplate = isComponentTemplate(tplRaw)
    ? tplRaw
    : (persisted?.template ?? 'classic');
  return {
    template,
    token: params.get('token') ?? persisted?.token ?? '',
    gameId: params.get('game_id') ?? persisted?.gameId ?? '',
  };
}

export interface ConstructorPageProps {
  iframeOrigin: string;
  /** Initial theme value. If provided, ConstructorProvider seeds reducer with this. */
  initialValue?: ConstructorValue;
  /** Restrict the template-switcher to a fixed template (operator-console: route param). */
  controlledTemplate?: ComponentTemplate;
  /** Called when user switches template via topbar radio (only relevant if controlledTemplate is not set, or to let parent react). */
  onTemplateChange?: (template: ComponentTemplate) => void;
  /** Hide token/game_id inputs from topbar (operator-console preview uses synthetic session). */
  hideSessionInputs?: boolean;
  /** Hide the template radio in topbar — caller provides its own selector in headerActions. */
  hideTemplateRadio?: boolean;
  /** Controlled token. If set, replaces internal state — TopBar reads this. */
  token?: string;
  /** Controlled game id. If set, replaces internal state. */
  gameId?: string;
  /** Called when token changes (parent can control it externally). */
  onTokenChange?: (v: string) => void;
  /** Called when game id changes (parent can control it externally). */
  onGameIdChange?: (v: string) => void;
  /** Rendered after the title in the topbar — for selectors (Template, Pack...). */
  leadingControls?: React.ReactNode;
  /** Rendered at the rightmost edge after Reset — for the primary action (e.g. Save). */
  trailingAction?: React.ReactNode;
  /** @deprecated Use `trailingAction` + `leadingControls` instead. */
  headerActions?: React.ReactNode;
  /** Sidebar visual style. Default 'dark' (iframe-app standalone). 'light' = friendlier on a host with light theme. */
  sidebarVariant?: 'dark' | 'light';
  /**
   * Open the custom pack lab on demo data, for an operator with no custom pack to pick: the preview always asks for a
   * lab state ("Live" being the lab on demo data, nothing seeded) and needs no game id.
   */
  labDemo?: boolean;
  /**
   * The previewed game is a custom pack: the lab is in play, so its group is listed in the sidebar before the preview
   * reports the lab, and the toggle offers every device preset. Unset (a regular pack), the sidebar lists the page
   * groups only and the toggle is the plain Desktop / Mobile pair in the frame it has always had. `labDemo` implies it.
   * It follows what is picked, not the preview's route: a route change must not reload the preview into another frame.
   */
  labActive?: boolean;
}

/** The lab state picked, and for which game: another game, or leaving the demo, starts over on the live lab. */
interface LabSceneChoice {
  gameId: string;
  labDemo: boolean;
  scene: LabPreviewSceneId;
}

/**
 * ConstructorPage — full-page constructor at `/design-system`. Layout:
 *   - Top bar: template radio + token/game_id inputs + Copy/Paste/Reset
 *   - Left sidebar (360px): DesignConstructorBody (Components only)
 *   - Main area: ConstructorPreview iframe (loads iframe-app in child mode)
 *     OR placeholder text if token/game_id missing
 */
export function ConstructorPage({
  iframeOrigin,
  initialValue,
  controlledTemplate,
  onTemplateChange,
  hideSessionInputs = false,
  hideTemplateRadio = false,
  token: externalToken,
  gameId: externalGameId,
  onTokenChange,
  onGameIdChange,
  leadingControls,
  trailingAction,
  headerActions,
  sidebarVariant = 'dark',
  labDemo = false,
  labActive = false,
}: ConstructorPageProps) {
  const initial = readInitial();
  const [template, setTemplate] = useState<ComponentTemplate>(controlledTemplate ?? initial.template);
  const [internalToken, setInternalToken] = useState(initial.token);
  const [internalGameId, setInternalGameId] = useState(initial.gameId);
  const [device, setDevice] = useState<PreviewDevice>('desktop');
  const selected = useSelectedComponent();
  const [iframeScene, setIframeScene] = useState<Scene | null>(null);
  useBaselineSync();

  const token = externalToken ?? internalToken;
  const gameId = externalGameId ?? internalGameId;
  const handleTokenChange = (v: string) => {
    onTokenChange?.(v);
    if (!externalToken) setInternalToken(v);
  };
  const handleGameIdChange = (v: string) => {
    onGameIdChange?.(v);
    if (!externalGameId) setInternalGameId(v);
  };

  const [labChoice, setLabChoice] = useState<LabSceneChoice | null>(null);
  const labScene =
    labChoice && labChoice.gameId === gameId && labChoice.labDemo === labDemo ? labChoice.scene : LIVE_LAB_SCENE;
  const chooseLabScene = (scene: LabPreviewSceneId) => setLabChoice({ gameId, labDemo, scene });
  // A real custom pack's live lab is the page as it is today, so nothing is asked for; the demo always asks.
  const previewLabScene = labDemo || labScene !== LIVE_LAB_SCENE ? labScene : null;
  // On the lab, and while a lab state is asked for (the preview reloading into it reports its route a moment later).
  const showLabScenes = iframeScene === 'custom-pack' || previewLabScene !== null;
  const canPreview = Boolean(token) && (Boolean(gameId) || labDemo);
  // Bumped to load the preview into its lab state again: a fresh iframe plays the state from its start.
  const [labRun, setLabRun] = useState(0);
  const labInPlay = labActive || labDemo;
  // Without the lab only Desktop and Mobile exist, whatever preset was picked while it was.
  const classicDevice: PreviewDevice = device === 'mobile' ? 'mobile' : 'desktop';

  const handleTemplateChange = (t: ComponentTemplate) => {
    if (controlledTemplate) onTemplateChange?.(t);
    else { setTemplate(t); onTemplateChange?.(t); }
  };

  useEffect(() => {
    if (controlledTemplate) setTemplate(controlledTemplate);
  }, [controlledTemplate]);

  useEffect(() => {
    if (controlledTemplate) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ template, token, gameId }));
    } catch {}
  }, [template, token, gameId, controlledTemplate]);

  const preview = canPreview ? (
    <ConstructorPreview
      key={labRun}
      token={token}
      gameId={gameId}
      template={template}
      iframeOrigin={iframeOrigin}
      onSceneChange={setIframeScene}
      labScene={previewLabScene}
    />
  ) : null;
  const placeholder = (
    <div className="flex h-full w-full items-center justify-center font-mono text-[12px] text-neutral-500">
      Enter token and game_id in the top bar to load preview
    </div>
  );

  return (
    <ConstructorProvider {...(initialValue ? { initialValue } : {})}>
      <div className="flex h-screen flex-col">
        <ConstructorTopBar
          template={template}
          onTemplateChange={handleTemplateChange}
          token={token}
          onTokenChange={handleTokenChange}
          gameId={gameId}
          onGameIdChange={handleGameIdChange}
          hideSessionInputs={hideSessionInputs}
          hideTemplateRadio={hideTemplateRadio}
          leadingControls={leadingControls}
          trailingAction={trailingAction}
          rightActions={headerActions}
        />

        <div className="flex flex-1 overflow-hidden">
          <aside
            className={
              'w-[360px] shrink-0 overflow-hidden border-r ' +
              (sidebarVariant === 'light'
                ? 'border-neutral-200 bg-neutral-50 text-neutral-900'
                : 'border-neutral-800 bg-neutral-950 text-white')
            }
          >
            <LabActiveContext value={labInPlay}>
              <DesignConstructorBody
                template={template}
                selectedComponent={selected?.name ?? null}
                {...(selected?.variant ? { selectedVariant: selected.variant } : {})}
                {...(selected ? { selectionNonce: selected.nonce } : {})}
                {...(iframeScene ? { currentScene: iframeScene } : {})}
              />
            </LabActiveContext>
          </aside>
          <main className="flex flex-1 flex-col overflow-hidden bg-neutral-950">
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 border-b border-neutral-800 bg-neutral-900 px-4 py-2">
              <DevicePreviewToggle
                value={labInPlay ? device : classicDevice}
                onChange={setDevice}
                {...(labInPlay ? { devices: ALL_PREVIEW_DEVICES } : {})}
              />
              {showLabScenes && (
                <LabSceneSelect
                  value={labScene}
                  onChange={chooseLabScene}
                  onReplay={previewLabScene !== null ? () => setLabRun((run) => run + 1) : undefined}
                />
              )}
            </div>
            {labInPlay ? (
              <DevicePreviewFrame device={device}>{preview ?? placeholder}</DevicePreviewFrame>
            ) : (
              <ClassicPreviewFrame device={classicDevice} empty={placeholder}>
                {preview}
              </ClassicPreviewFrame>
            )}
          </main>
        </div>
      </div>
    </ConstructorProvider>
  );
}
